import React from 'react';
import { useSelector } from 'react-redux';
import { Chip } from '@milanote/roma';

import { holdFocusWithoutKeyboard } from '../../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import '../../task/pills/assignment/ChipPopover.scss';
import ChipSelectionPopover from '../selectionViews/ChipSelectionPopover';
import ChipSelectionSheet from '../selectionViews/ChipSelectionSheet';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';

export type RenderAssignmentPopupContent = (args: { closePopup: () => void }) => React.ReactNode;

type AssignmentChipViewProps = {
    chipRef: React.RefObject<HTMLSpanElement>;
    size: ChipBaseProps['size'];
    variant: 'normal' | 'user';
    children: React.ReactNode;
    containerId: string;
    isSelected: boolean;
    isDisabled: boolean;
    isSearchHighlighted?: boolean;
    deselect: () => void;
    renderPopupContent: RenderAssignmentPopupContent;
    chipProps: React.HTMLAttributes<HTMLSpanElement> & {
        role: React.AriaRole;
        tabIndex: number;
        className: string;
    };
};

/**
 * Shared view for the assignment and assignment-placeholder chips: the pill
 * plus its selection-owned assignee picker (popover on desktop, sheet on
 * mobile, mirroring `DueDateChipView`).
 */
const AssignmentChipView: React.FC<AssignmentChipViewProps> = ({
    chipRef,
    size,
    variant,
    children,
    containerId,
    isSelected,
    isDisabled,
    isSearchHighlighted,
    deselect,
    renderPopupContent,
    chipProps,
}) => {
    const isMobileAppMode = useSelector(selectIsMobileAppMode);

    return (
        <>
            <Chip
                ref={chipRef}
                {...chipProps}
                size={size}
                variant={variant}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isSearchHighlighted={isSearchHighlighted}
            >
                {children}
            </Chip>

            {isMobileAppMode ? (
                <ChipSelectionSheet
                    sheetId={SheetId.AssignmentTool}
                    instanceId={containerId}
                    className="assignment-tool"
                    isOpen={isSelected}
                    onOpenChange={(isOpen) => !isOpen && deselect()}
                    onCloseTransitionStart={holdFocusWithoutKeyboard}
                >
                    {(close) => renderPopupContent({ closePopup: close })}
                </ChipSelectionSheet>
            ) : (
                <ChipSelectionPopover
                    triggerRef={chipRef}
                    isOpen={isSelected}
                    onOpenChange={(isOpen) => !isOpen && deselect()}
                    popoverClassName="chip-popover"
                >
                    {(close) => renderPopupContent({ closePopup: close })}
                </ChipSelectionPopover>
            )}
        </>
    );
};

export default AssignmentChipView;
